// src/lib/signOutCleanup.js — what this browser must forget when an agent signs out
// (security review 2026-09-17).
//
// Sign-out used to end the session and nothing else. The contract autosave (parties' ID numbers,
// kept 14 days), cooperation and share drafts, share payloads, the recently-viewed / recently-
// searched lists and the Desk's IndexedDB listing cache all stayed readable in DevTools by whoever
// used the browser profile next — the shared office computer.
//
// ⚠️ An ALLOW-LIST OF WHAT TO REMOVE, not `localStorage.clear()`: theme, language, view
// preferences and panel memory carry no client data and an agent signing back in expects them.
// A NEW store that holds client or listing data must add its prefix HERE.
//
// Every access is wrapped: storage can be blocked, and a failed cleanup must never stop the
// sign-out itself. PURE apart from the injected storage / IndexedDB factory.
export const SENSITIVE_PREFIXES = [
  'sntv.contractProgress.',   // lib/contractAutosave.js — party ID numbers
  'sntv_coop_draft:',         // CooperationModal — owner terms, prices
  'sntv_share_draft:',        // ShareTab — edited listing fields
  'sntv_share_payload:',      // ShareTab — cached listing payloads
  'desk_recent_viewed:',      // IntakeTab — which properties this agent opened
  'desk_recent_searches:',    // IntakeTab — what this agent searched for
  // db/deskCache.js — listing pages, the client/demand feed (hist:), agent books, pins and
  // counts (review 2026-09-23). Every version (`sntv_desk_v2:` today): the next agent at a
  // shared computer was painted the previous user's cached feed before their own fetch landed.
  'sntv_desk_',
]
export const SENSITIVE_DATABASES = ['sntv_desk_cache']   // db/queryCache.js

/** Remove every sensitive key. Returns how many were removed. */
export function clearSensitiveStorage(storage) {
  if (!storage) return 0
  let keys = []
  try { for (let i = 0; i < storage.length; i++) { const k = storage.key(i); if (k) keys.push(k) } } catch { return 0 }
  keys = keys.filter(k => SENSITIVE_PREFIXES.some(p => k.startsWith(p)))
  let n = 0
  for (const k of keys) { try { storage.removeItem(k); n++ } catch { /* blocked */ } }
  return n
}

/** Delete the Desk's IndexedDB caches. Resolves when done, blocked or unavailable — never rejects. */
export function deleteSensitiveDatabases(idb) {
  if (!idb) return Promise.resolve(false)
  return Promise.all(SENSITIVE_DATABASES.map(name => new Promise(resolve => {
    let req
    try { req = idb.deleteDatabase(name) } catch { return resolve(false) }
    req.onsuccess = () => resolve(true)
    req.onerror = () => resolve(false)
    // Another tab holds it open: the delete is queued and completes when that tab closes or
    // reloads (which sign-out causes). Do not make the agent wait for it.
    req.onblocked = () => resolve(false)
  }))).then(r => r.every(Boolean))
}
